export interface ResponseEvaluation {
  score: number; // 0-100
  quality: 'ruim' | 'boa' | 'ótima';
  qualityEn: 'poor' | 'good' | 'excellent';
  details: {
    length: number;
    hasStructure: boolean;
    hasSpecificInfo: boolean;
    coherence: number;
    completeness: number;
  };
}

export const evaluateResponse = (response: string): ResponseEvaluation => {
  if (!response || response.trim().length === 0) {
    return {
      score: 0,
      quality: 'ruim',
      qualityEn: 'poor',
      details: {
        length: 0,
        hasStructure: false,
        hasSpecificInfo: false,
        coherence: 0,
        completeness: 0
      }
    };
  }

  const cleanResponse = response.trim();
  const wordCount = cleanResponse.split(/\s+/).filter(word => word.length > 0).length;
  
  let score = 0;
  let lengthScore = 0;
  let structureScore = 0;
  let specificityScore = 0;
  let coherenceScore = 0;
  let completenessScore = 0;

  // 1. Length evaluation (25 points)
  if (wordCount >= 50) lengthScore = 25;
  else if (wordCount >= 20) lengthScore = 15;
  else if (wordCount >= 10) lengthScore = 8;
  else if (wordCount >= 5) lengthScore = 3;
  
  // 2. Structure evaluation (20 points)
  const hasLists = /[-*•]\s|^\d+\.|^\w+\)/.test(cleanResponse);
  const hasParagraphs = cleanResponse.split('\n\n').length > 1;
  const hasHeaders = /#|==|\*\*/.test(cleanResponse);
  const hasCodeBlocks = /```|`[^`]+`/.test(cleanResponse);
  
  if (hasCodeBlocks) structureScore += 8;
  if (hasHeaders) structureScore += 6;
  if (hasLists) structureScore += 4;
  if (hasParagraphs) structureScore += 2;
  structureScore = Math.min(structureScore, 20);

  // 3. Specificity evaluation (25 points)
  const hasNumbers = /\d+/.test(cleanResponse);
  const hasExamples = /exemplo|example|por exemplo|for example|como:|such as:/i.test(cleanResponse);
  const hasDetails = /porque|why|como|how|quando|when|onde|where/i.test(cleanResponse);
  const hasActionWords = /deve|should|pode|can|precisa|need|recomendo|recommend/i.test(cleanResponse);
  
  if (hasNumbers) specificityScore += 6;
  if (hasExamples) specificityScore += 8;
  if (hasDetails) specificityScore += 6;
  if (hasActionWords) specificityScore += 5;
  specificityScore = Math.min(specificityScore, 25);

  // 4. Coherence evaluation (15 points)
  const sentences = cleanResponse.split(/[.!?]+/).filter(s => s.trim().length > 0);
  if (sentences.length >= 3) coherenceScore = 15;
  else if (sentences.length >= 2) coherenceScore = 10;
  else if (sentences.length >= 1) coherenceScore = 5;

  // 5. Completeness evaluation (15 points)
  const hasConclusion = /conclusão|conclusion|resumo|summary|portanto|therefore|em suma|in sum/i.test(cleanResponse);
  const hasIntroduction = cleanResponse.length > 50 && cleanResponse.split(' ').slice(0, 10).join(' ').length > 0;
  const hasMiddleContent = wordCount > 30;
  
  if (hasConclusion) completenessScore += 6;
  if (hasIntroduction) completenessScore += 4;
  if (hasMiddleContent) completenessScore += 5;
  completenessScore = Math.min(completenessScore, 15);

  // Calculate final score
  score = lengthScore + structureScore + specificityScore + coherenceScore + completenessScore;

  // Determine quality level
  let quality: 'ruim' | 'boa' | 'ótima';
  let qualityEn: 'poor' | 'good' | 'excellent';
  
  if (score >= 75) {
    quality = 'ótima';
    qualityEn = 'excellent';
  } else if (score >= 50) {
    quality = 'boa';
    qualityEn = 'good';
  } else {
    quality = 'ruim';
    qualityEn = 'poor';
  }

  return {
    score: Math.round(score),
    quality,
    qualityEn,
    details: {
      length: wordCount,
      hasStructure: structureScore > 10,
      hasSpecificInfo: specificityScore > 15,
      coherence: Math.round((coherenceScore / 15) * 100),
      completeness: Math.round((completenessScore / 15) * 100)
    }
  };
};